/**
 * Offline Navigation Service
 * 
 * Provides offline-capable navigation features:
 * - Caches route data in IndexedDB
 * - Stores well location data for compass navigation
 * - Provides GPS-only navigation when offline
 * - Syncs data when back online
 */

// Types for cached navigation data
export interface CachedRoute {
    id: string;
    wellId: string;
    wellName: string;
    destination: { lat: number; lng: number };
    steps: Array<{
        instruction: string;
        distance: number;
        maneuver: string;
        startLocation: { lat: number; lng: number };
    }>;
    totalDistance: number;
    totalDuration: number;
    polyline?: string;
    cachedAt: Date;
    expiresAt: Date;
}

export interface CachedWellLocation {
    id: string;
    name: string;
    apiNumber?: string;
    location: { lat: number; lng: number };
    gateCode?: string;
    accessNotes?: string;
    cachedAt: Date;
}

const DB_NAME = 'oilwellx-navigation';
const DB_VERSION = 1;
const ROUTES_STORE = 'cached_routes';
const WELLS_STORE = 'cached_wells';
const CACHE_DURATION_HOURS = 24;

class OfflineNavigationService {
    private db: IDBDatabase | null = null;
    private isOnline: boolean = navigator.onLine;

    constructor() {
        // Listen for online/offline events
        window.addEventListener('online', () => {
            this.isOnline = true;
            this.syncCachedData();
        });

        window.addEventListener('offline', () => {
            this.isOnline = false;
        });
    }

    /**
     * Initialize IndexedDB
     */
    async initialize(): Promise<void> {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION);

            request.onerror = () => {
                console.error('Failed to open IndexedDB:', request.error);
                reject(request.error);
            };

            request.onsuccess = () => {
                this.db = request.result;
                resolve();
            };

            request.onupgradeneeded = (event) => {
                const db = (event.target as IDBOpenDBRequest).result;

                // Create routes store
                if (!db.objectStoreNames.contains(ROUTES_STORE)) {
                    const routesStore = db.createObjectStore(ROUTES_STORE, { keyPath: 'id' });
                    routesStore.createIndex('wellId', 'wellId', { unique: false });
                    routesStore.createIndex('expiresAt', 'expiresAt', { unique: false });
                }

                // Create wells store
                if (!db.objectStoreNames.contains(WELLS_STORE)) {
                    const wellsStore = db.createObjectStore(WELLS_STORE, { keyPath: 'id' });
                    wellsStore.createIndex('name', 'name', { unique: false });
                }
            };
        });
    }

    /**
     * Cache a route for offline use
     */
    async cacheRoute(route: Omit<CachedRoute, 'cachedAt' | 'expiresAt'>): Promise<void> {
        if (!this.db) await this.initialize();

        const cachedRoute: CachedRoute = {
            ...route,
            cachedAt: new Date(),
            expiresAt: new Date(Date.now() + CACHE_DURATION_HOURS * 60 * 60 * 1000),
        };

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([ROUTES_STORE], 'readwrite');
            const store = transaction.objectStore(ROUTES_STORE);
            const request = store.put(cachedRoute);

            request.onsuccess = () => {
                resolve();
            };
            request.onerror = () => reject(request.error);
        });
    }

    /**
     * Get cached route for a well
     */
    async getCachedRoute(wellId: string): Promise<CachedRoute | null> {
        if (!this.db) await this.initialize();

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([ROUTES_STORE], 'readonly');
            const store = transaction.objectStore(ROUTES_STORE);
            const index = store.index('wellId');
            const request = index.getAll(wellId);

            request.onsuccess = () => {
                const routes = request.result as CachedRoute[];
                // Return most recent non-expired route
                const validRoute = routes
                    .filter(r => new Date(r.expiresAt) > new Date())
                    .sort((a, b) => new Date(b.cachedAt).getTime() - new Date(a.cachedAt).getTime())[0];

                resolve(validRoute || null);
            };
            request.onerror = () => reject(request.error);
        });
    }

    /**
     * Cache well location for offline compass navigation
     */
    async cacheWellLocation(well: Omit<CachedWellLocation, 'cachedAt'>): Promise<void> {
        if (!this.db) await this.initialize();

        const cachedWell: CachedWellLocation = {
            ...well,
            cachedAt: new Date(),
        };

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([WELLS_STORE], 'readwrite');
            const store = transaction.objectStore(WELLS_STORE);
            const request = store.put(cachedWell);

            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    }

    /**
     * Get cached well location
     */
    async getCachedWellLocation(wellId: string): Promise<CachedWellLocation | null> {
        if (!this.db) await this.initialize();

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([WELLS_STORE], 'readonly');
            const store = transaction.objectStore(WELLS_STORE);
            const request = store.get(wellId);

            request.onsuccess = () => resolve(request.result || null);
            request.onerror = () => reject(request.error);
        });
    }

    /**
     * Get all cached wells for offline access
     */
    async getAllCachedWells(): Promise<CachedWellLocation[]> {
        if (!this.db) await this.initialize();

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([WELLS_STORE], 'readonly');
            const store = transaction.objectStore(WELLS_STORE);
            const request = store.getAll();

            request.onsuccess = () => resolve(request.result || []);
            request.onerror = () => reject(request.error);
        });
    }

    /**
     * Pre-cache wells for a user's assigned routes
     */
    async preCacheAssignedWells(wells: Array<{
        id: string;
        name: string;
        apiNumber?: string;
        latitude: number;
        longitude: number;
        gateCode?: string;
        accessNotes?: string;
    }>): Promise<number> {
        let cached = 0;

        for (const well of wells) {
            try {
                await this.cacheWellLocation({
                    id: well.id,
                    name: well.name,
                    apiNumber: well.apiNumber,
                    location: { lat: well.latitude, lng: well.longitude },
                    gateCode: well.gateCode,
                    accessNotes: well.accessNotes,
                });
                cached++;
            } catch (error) {
                console.error('Error caching well:', well.name, error);
            }
        }

        return cached;
    }

    /**
     * Clear expired cache entries
     */
    async clearExpiredCache(): Promise<number> {
        if (!this.db) await this.initialize();

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([ROUTES_STORE], 'readwrite');
            const store = transaction.objectStore(ROUTES_STORE);
            const index = store.index('expiresAt');
            const now = new Date();
            const range = IDBKeyRange.upperBound(now);
            const request = index.openCursor(range);

            let deleted = 0;

            request.onsuccess = (event) => {
                const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
                if (cursor) {
                    cursor.delete();
                    deleted++;
                    cursor.continue();
                } else {
                    resolve(deleted);
                }
            };
            request.onerror = () => reject(request.error);
        });
    }

    /**
     * Sync cached data when back online
     */
    private async syncCachedData(): Promise<void> {
        // Clear expired entries
        await this.clearExpiredCache();

        // In future: could sync any offline-created data back to server
    }

    /**
     * Calculate bearing between two points (for offline compass)
     */
    calculateBearing(
        from: { lat: number; lng: number },
        to: { lat: number; lng: number }
    ): number {
        const lat1 = this.toRadians(from.lat);
        const lat2 = this.toRadians(to.lat);
        const dLng = this.toRadians(to.lng - from.lng);

        const y = Math.sin(dLng) * Math.cos(lat2);
        const x = Math.cos(lat1) * Math.sin(lat2) -
            Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);

        const bearing = Math.atan2(y, x);
        return (this.toDegrees(bearing) + 360) % 360;
    }

    /**
     * Calculate distance between two points (Haversine formula)
     */
    calculateDistance(
        from: { lat: number; lng: number },
        to: { lat: number; lng: number }
    ): number {
        const R = 6371000; // Earth's radius in meters
        const lat1 = this.toRadians(from.lat);
        const lat2 = this.toRadians(to.lat);
        const dLat = this.toRadians(to.lat - from.lat);
        const dLng = this.toRadians(to.lng - from.lng);

        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1) * Math.cos(lat2) *
            Math.sin(dLng / 2) * Math.sin(dLng / 2);

        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

        return R * c; // Distance in meters
    }

    private toRadians(degrees: number): number {
        return degrees * (Math.PI / 180);
    }

    private toDegrees(radians: number): number {
        return radians * (180 / Math.PI);
    }

    /**
     * Check if currently offline
     */
    isOffline(): boolean {
        return !this.isOnline;
    }
}

// Singleton export
export const offlineNavigationService = new OfflineNavigationService();
